// The local database: Dexie over IndexedDB.
//
// Designed for IndexedDB idioms rather than translated from SQL: there are no joins,
// so everything a list row needs is denormalised onto the contact row and maintained
// in the same transaction that writes events. Times are epoch milliseconds; absent
// values are null (IndexedDB-friendly), while parsed wire models use undefined.

import Dexie, { type EntityTable } from 'dexie';

import type { EntryStatus } from '$lib/protocol/types';

export interface ContactRow {
  /** The account id at the company's server — the primary key everywhere. */
  accountId: string;
  entryKey: string;
  phone: string;
  /** Trading name from the session response — the directory does not carry names. */
  name: string;
  serverUrl: string;
  /** The server key currently published in the directory. */
  serverKey: string;
  /** The key pinned when the contact was added; a difference pauses sending until
   *  the user trusts the new key. */
  pinnedServerKey: string;
  status: EntryStatus;
  successorKey: string | null;
  successorAccountId: string | null;
  successorSigned: boolean;
  changePendingUntilMs: number | null;
  sendingPaused: boolean;
  /** Session refresh failed terminally — the user has to verify by SMS again.
   *  History stays visible the whole time. */
  needsReverify: boolean;
  threadId: string | null;
  deviceId: string | null;
  token: string | null;
  tokenExpiresMs: number | null;
  quarantineUntilMs: number | null;
  quarantineRestrictions: string[];
  /** Event-stream cursor — where the next fetch starts. */
  cursor: string | null;
  lastSequence: number;
  /** The per-relation notification key handed to this distributor so its wakes
   *  may carry an encrypted preview. Generated on first capability renewal;
   *  null until push is enabled. Non-indexed, so no schema version bump. */
  notificationKeyB64: string | null;
  /** The `views_version` behind the page list currently cached for this contact. Null
   *  until a thread state carrying one has been seen. Persisted so that reopening the
   *  app does not refetch a menu that has not changed. Non-indexed, so no schema
   *  version bump of its own. */
  viewsVersion: number | null;
  addedAtMs: number;
  // ——— denormalised for the threads list ———
  unreadCount: number;
  pendingCount: number;
  lastEventTimeMs: number | null;
  /** Sequence of the event behind the preview — guards against an older page,
   *  replayed during a resync, overwriting a newer preview. */
  lastEventSequence: number;
  /** The raw JSON of the last event's elements, for the list preview. */
  lastEventJson: string | null;
  lastFromUser: boolean;
  /** Newest queued outbox item — the list orders by the newest of event/outbox. */
  lastOutboxTimeMs: number | null;
}

export interface EventRow {
  id: string;
  accountId: string;
  sequence: number;
  timeMs: number;
  authorKind: string;
  authorName: string | null;
  clientUuid: string | null;
  /** The `elements` array exactly as served, JSON-encoded verbatim. */
  elementsJson: string;
  isRead: boolean;
}

export interface OutboxRow {
  uuid: string;
  accountId: string;
  kind: 'message' | 'cart_response';
  /** For messages: the wire `elements` array. For cart responses: the request body. */
  payloadJson: string;
  /** A `pendingMedia` row id when the message carries bytes not yet uploaded. */
  pendingMediaId: string | null;
  mediaType: string | null;
  createdAtMs: number;
  attempts: number;
  nextAttemptMs: number;
  lastErrorCode: string | null;
  permanentlyFailed: boolean;
}

export interface PendingMediaRow {
  id: string;
  bytes: ArrayBuffer;
  contentType: string;
  createdAtMs: number;
}

export interface DeviceKeyRow {
  /** `dialano_key_<accountId>` — the alias scheme of the native client. */
  alias: string;
  /** Non-extractable — survives IndexedDB via structured clone; the private half
   *  can be used but never read. Software protection, honestly: the web platform
   *  has no secure element to offer. */
  privateKey: CryptoKey;
  publicKeyB64: string;
  keyId: string;
  createdAtMs: number;
}

export interface DirectoryEntryRow {
  entryKey: string;
  sectionPrefix: string;
  /** The entry exactly as served — proofs hash over its canonical form. */
  rawJson: string;
  /** Known only when someone typed the number that hashed to this key. */
  phone: string | null;
  updatedAtMs: number;
}

export interface ManifestRow {
  id: number;
  /** The verified manifest body, JSON-encoded (content already threshold-checked). */
  bodyJson: string;
  version: number;
  validUntilMs: number;
  fetchedAtMs: number;
  fromDomain: string;
}

export interface MuteRow {
  accountId: string;
  category: string;
}

export interface CalendarEntryRow {
  /** `<accountId>/<eventId>`. */
  id: string;
  accountId: string;
  eventId: string;
  /** Sequence of the event this state came from — a lower one never overwrites. */
  sourceSequence: number;
  elementJson: string;
}

export interface MediaCacheRow {
  mediaId: string;
  accountId: string;
  bytes: ArrayBuffer;
  contentType: string;
  sizeB: number;
  lastUsedMs: number;
}

export interface ViewRow {
  /** `<accountId>/<viewId>` — Dexie needs a single-value key for `put`. */
  id: string;
  accountId: string;
  viewId: string;
  title: string;
  version: number;
  /** Menu order as the server listed it. */
  position: number;
  /** The `elements` array exactly as served, JSON-encoded verbatim — parsed on read,
   *  the same contract the event rows follow. */
  elementsJson: string | null;
  /** When the body was last fetched successfully. Null while only the descriptor is
   *  known, which is the ordinary state right after a list refresh. */
  fetchedAtMs: number | null;
}

export interface SettingRow {
  key: string;
  value: unknown;
}

export class AppDb extends Dexie {
  contacts!: EntityTable<ContactRow, 'accountId'>;
  events!: EntityTable<EventRow, 'id'>;
  outbox!: EntityTable<OutboxRow, 'uuid'>;
  pendingMedia!: EntityTable<PendingMediaRow, 'id'>;
  deviceKeys!: EntityTable<DeviceKeyRow, 'alias'>;
  directoryEntries!: EntityTable<DirectoryEntryRow, 'entryKey'>;
  manifest!: EntityTable<ManifestRow, 'id'>;
  mutes!: Dexie.Table<MuteRow, [string, string]>;
  calendarEntries!: EntityTable<CalendarEntryRow, 'id'>;
  mediaCache!: EntityTable<MediaCacheRow, 'mediaId'>;
  settings!: EntityTable<SettingRow, 'key'>;
  views!: EntityTable<ViewRow, 'id'>;

  constructor() {
    super('dialano');
    this.version(1).stores({
      contacts: 'accountId, entryKey',
      events: 'id, [accountId+sequence]',
      outbox: 'uuid, accountId, nextAttemptMs',
      pendingMedia: 'id',
      deviceKeys: 'alias',
      directoryEntries: 'entryKey, sectionPrefix',
      manifest: 'id',
      mutes: '[accountId+category]',
      calendarEntries: 'id, accountId',
      mediaCache: 'mediaId, lastUsedMs, accountId',
      settings: 'key',
    });
    // Version 2 adds one store and touches nothing else. The version(1) block above must
    // stay exactly as it is: every installed app already carries data described by it, and
    // editing it in place would ask Dexie to re-derive stores that already exist. Adding a
    // store in a new version needs no upgrade function — the old ones are carried over.
    this.version(2).stores({
      views: 'id, accountId, [accountId+viewId]',
    });
  }
}

export const db = new AppDb();
